import { useEffect } from 'react'
import { setMetaTags } from '@/utils/seo'
import { Link } from 'react-router-dom'
import { MapPin, CheckCircle, ChevronRight } from 'lucide-react'
import SiluxPromise from '@/components/SiluxPromise'

const SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "name": "Mobile Broadband for Caravans & Motorhomes",
      "provider": { "@type": "Organization", "name": "Silux Mobile", "url": "https://www.siluxmobile.co.uk" },
      "serviceType": "Mobile Broadband",
      "areaServed": "United Kingdom",
      "description": "4G and 5G mobile broadband for caravans, motorhomes and touring. Router included and delivered, works anywhere Three, O2 or Vodafone has signal, and travels with you. No landline and no engineer needed. Plans from 1 month (rolling) to 36 months, from £15.95 a month. No price rises during your contract and free broadband upgrades.",
      "url": "https://www.siluxmobile.co.uk/broadband-caravan"
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "What is the best SIM card for a caravan?", "acceptedAnswer": { "@type": "Answer", "text": "A data SIM card on Three's network is the best option for caravans. Three has the widest rural coverage of any UK network, and Silux Mobile's data SIMs start from £5/month, on a 30-day rolling plan or a 3 to 24-month contract — perfect for touring." } },
        { "@type": "Question", "name": "How do I get WiFi in a caravan?", "acceptedAnswer": { "@type": "Answer", "text": "The easiest way is a MiFi device with a data SIM card. Insert a Silux Mobile data SIM into a MiFi router and it creates a portable WiFi hotspot that works anywhere Three has 4G or 5G signal." } },
        { "@type": "Question", "name": "Does Three work in rural areas?", "acceptedAnswer": { "@type": "Answer", "text": "Three covers over 99% of the UK population, including most rural areas, national parks and caravan sites. You can check coverage at siluxmobile.co.uk/network-coverage before buying." } },
        { "@type": "Question", "name": "Can I use a caravan SIM card abroad?", "acceptedAnswer": { "@type": "Answer", "text": "Yes — Silux Mobile SIMs work abroad with Three Go Roam in 71+ countries, including France, Spain, Germany and the USA. Roaming is not free: roaming charges vary from package to package, so check the roaming charges for your plan before you travel." } },
        { "@type": "Question", "name": "How much data does a caravan need?", "acceptedAnswer": { "@type": "Answer", "text": "Every Silux Mobile broadband plan has unlimited data with no speed throttling, so you can browse, stream and work from the caravan (fair use policy applies). 5G Premium starts at £19.95/month on a 36-month plan." } },
        { "@type": "Question", "name": "How long do I have to take a caravan broadband plan for?", "acceptedAnswer": { "@type": "Answer", "text": "You choose the term, from 1 month up to 36. Broadband Lite is available on a rolling monthly contract from £25.95/month (1-month minimum term, 30 days’ notice to leave, £30 refundable holding deposit), or on fixed terms of 3, 6, 12, 18, 24 or 36 months. The MiFi Portable is £15.95 a month over 24 months (£17.50 over 12), and 3-month plans start at £19.95 a month plus a £15 refundable holding deposit. There is no setup fee on any plan." } },
        { "@type": "Question", "name": "Will my broadband price increase during my contract?", "acceptedAnswer": { "@type": "Answer", "text": "No. Your price is fixed for the life of your plan — there are no price increases during your contract and no annual inflation-linked price rises. What you sign up at is what you pay." } },
        { "@type": "Question", "name": "Are broadband upgrades free?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Silux Mobile offers free broadband upgrades — move to a faster plan or upgrade your router with no upgrade fee. Free upgrades are included as standard." } }
      ]
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.siluxmobile.co.uk" },
        { "@type": "ListItem", "position": 2, "name": "Broadband", "item": "https://www.siluxmobile.co.uk/broadband" },
        { "@type": "ListItem", "position": 3, "name": "Caravan & Motorhome Broadband", "item": "https://www.siluxmobile.co.uk/broadband-caravan" }
      ]
    }
  ]
}

const PLANS = [
  { name: "Rolling Monthly", data: "1 month", price: "£25.95", best: "Tour as you go", features: ["1-month minimum, then month to month", "30 days’ notice to leave", "£30 refundable holding deposit", "4G router included"] },
  { name: "Touring Starter", data: "3 months", price: "£19.95", best: "One season", features: ["3-month plan", "£15 refundable holding deposit", "4G/5G router included", "Roaming extra — varies by package"] },
  { name: "MiFi Portable", data: "Pocket-sized", price: "£15.95", best: "Full-time tourers", features: ["24-month plan — best rate", "12 months at £17.50", "Portable MiFi included", "Roaming extra — varies by package"], popular: true },
  { name: "5G Premium", data: "Unlimited", price: "£19.95", best: "Working from the van", features: ["36-month plan", "Unlimited data, 5G where available", "5G router included", "Roaming extra — varies by package"] },
]

const SITES = [
  "Lake District National Park", "Peak District", "Yorkshire Dales", "Norfolk Broads",
  "Dartmoor", "Exmoor", "New Forest", "Brecon Beacons",
  "Scottish Highlands", "Snowdonia", "North Wales Coast", "Cornwall",
]

export default function BroadbandCaravan() {
  useEffect(() => {
    setMetaTags({
      title: 'Caravan & Motorhome Broadband UK | Portable 4G/5G WiFi | Silux Mobile',
      description: "Broadband for caravans, motorhomes and campervans. Portable 4G & 5G MiFi internet from £15.95/month with the router included — no landline, no installation, and it travels with you. Plans from 1 month (rolling) to 36 months.",
      keywords: ['caravan broadband', 'motorhome wifi', 'campervan internet', 'portable broadband UK', 'mifi broadband', 'travel wifi UK', 'mobile broadband UK'],
      canonical: 'https://www.siluxmobile.co.uk/broadband-caravan',
    })
    const s = document.createElement('script')
    s.type = 'application/ld+json'
    s.text = JSON.stringify(SCHEMA)
    document.head.appendChild(s)
    return () => { document.head.removeChild(s) }
  }, [])

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-green-700 to-green-900 text-white py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <nav className="text-green-300 text-sm mb-6 flex items-center gap-2">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/broadband" className="hover:text-white">Broadband</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Caravan &amp; Motorhome</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Caravan &amp; Motorhome Broadband UK
          </h1>
          <p className="text-xl text-green-100 mb-3 max-w-2xl">
            Stay connected wherever you travel. A Silux Mobile data SIM in the MiFi device we send you gives you reliable 4G and 5G internet at any caravan site, motorhome stopover or wild camp — no landline, no engineer, no setup fee. From £15.95 a month.
          </p>
          <p className="text-green-200 text-base mb-8 max-w-2xl">
            A <strong>caravan SIM card</strong> is a data SIM you put into a MiFi router or hotspot device. It pulls a mobile internet signal from the nearest tower and broadcasts WiFi to all your devices — TV stick, laptop, phones, tablets — exactly like home broadband but completely portable.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/broadband" className="bg-white text-green-800 font-semibold px-6 py-3 rounded-lg hover:bg-green-50 transition">
              See Broadband Plans
            </Link>
            <Link to="/data-sim-cards" className="border border-white text-white px-6 py-3 rounded-lg hover:bg-white/10 transition">
              Data SIM Cards
            </Link>
          </div>
        </div>
      </section>

      <SiluxPromise variant="broadband" />

      {/* Why mobile beats caravan site WiFi */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Why Not Use the Caravan Site WiFi?</h2>
          <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
            Site WiFi is shared between every pitch. During peak season it's often too slow to stream video, and it rarely reaches every corner of the site.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Your Own Private Connection", body: "A mobile data SIM is yours alone. Full speed, no sharing with 200 other pitches." },
              { title: "Works Off-Site Too", body: "Travelling, at a services, parked up somewhere scenic — your connection goes with you. Site WiFi doesn't." },
              { title: "No Setup, No Passwords", body: "Plug the MiFi in, connect your devices. Done. Works the moment you arrive anywhere with signal." },
            ].map(c => (
              <div key={c.title} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-2">{c.title}</h3>
                <p className="text-gray-600 text-sm">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Caravan Broadband Plans</h2>
          <p className="text-gray-500 text-center mb-10">All plans run on Three's 4G/5G network. Plans from 1 month rolling to 36 months, no setup fee. Roaming is an extra cost and varies from package to package.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {PLANS.map(plan => (
              <div key={plan.name} className={`rounded-2xl p-6 border-2 ${plan.popular ? 'border-green-500 shadow-lg' : 'border-gray-200'} relative`}>
                {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">MOST POPULAR</div>}
                <p className="text-sm text-gray-500 mb-1">{plan.best}</p>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                <p className="text-3xl font-bold text-green-700 mb-4">{plan.price}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                <ul className="space-y-2 mb-6">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/broadband" className={`block text-center py-2.5 rounded-lg font-semibold text-sm transition ${plan.popular ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>
                  Get This Plan
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Go Roam abroad */}
      <section className="py-14 px-4 bg-green-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Taking the Caravan Abroad?</h2>
          <p className="text-gray-600 mb-6 max-w-xl mx-auto">
            Silux Mobile SIMs work abroad with <strong>Three Go Roam</strong> in 71+ countries including France, Spain, Italy, Germany, Portugal and the Netherlands. <strong>Roaming is an extra cost</strong> and varies from package to package — check the roaming charges for your plan before you travel.
          </p>
          <Link to="/blog/three-go-roam-explained" className="text-green-700 font-semibold hover:underline">
            See all 71 Go Roam countries →
          </Link>
        </div>
      </section>

      {/* Coverage at popular sites */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-3 text-center">Coverage at Popular Caravan Destinations</h2>
          <p className="text-gray-500 text-center mb-8">Three's network covers the vast majority of UK caravan and camping sites.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {SITES.map(site => (
              <div key={site} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <MapPin className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">{site}</span>
              </div>
            ))}
          </div>
          <p className="text-center mt-6">
            <Link to="/network-coverage" className="text-green-700 font-semibold hover:underline">Check your exact pitch location →</Link>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Caravan Broadband FAQ</h2>
          <div className="space-y-4">
            {(SCHEMA["@graph"][1] as any).mainEntity.map((q: any) => (
              <details key={q.name} className="bg-white rounded-xl border border-gray-200 p-5 group">
                <summary className="font-semibold text-gray-900 cursor-pointer list-none flex justify-between items-center">
                  {q.name}
                  <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="text-gray-600 text-sm mt-3">{q.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-14 px-4 bg-green-700 text-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-center">Explore More from Silux Mobile</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { to: "/data-sim-cards", label: "Data SIM Cards" },
              { to: "/mobile-broadband", label: "Mobile Broadband" },
              { to: "/cheap-sim-cards", label: "Cheap SIM Cards" },
              { to: "/no-contract-sim", label: "No Contract SIM" },
            ].map(l => (
              <Link key={l.to} to={l.to} className="bg-white/10 hover:bg-white/20 rounded-lg px-4 py-3 text-center text-sm font-medium transition">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
